import { keyframes, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { MEGA_NAV_GEOMETRY } from './megaNav.geometry';

/**
 * Gold, red, teal, blue: the approved "Twinkle lights v2" bulb colors, in the order each bulb cycles
 * through them. Decorative and outside the theme palette, so they're defined once, here.
 */
export const BULB_COLORS = ['#ffa133', '#d7425a', '#00948a', '#25affa'] as const;
const [gold, red, teal, blue] = BULB_COLORS;

const bulbColorCycle = keyframes`
  0%, 22% { fill: ${gold}; }
  25%, 47% { fill: ${red}; }
  50%, 72% { fill: ${teal}; }
  75%, 97% { fill: ${blue}; }
  100% { fill: ${gold}; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.05; }
`;

const BULB_CYCLE_S = 2.4;
const GLOW_PULSE_S = 1.2;

/**
 * The lights play for at most 5s, then hold still: WCAG 2.2.2 exempts auto-playing motion only when it
 * lasts 5s or less, so neither stop may exceed 5. The negative delays start each animation partway in,
 * so each needs its own iteration count to stop at the right moment. Every duration and delay below
 * derives from these constants, so the stops stay correct if one changes.
 * - Bulbs stop at 5s, which lands every bulb inside its starting color's hold, so they rest on the
 *   same colors as the reduced-motion state.
 * - Glows stop at 4.5s, which puts group a at 75% and group b at 25% of the pulse: equal opacity.
 */
const BULB_STOP_S = 5;
const GLOW_STOP_S = 4.5;
const iterationsUntil = (stopS: number, durationS: number, delayS: number) => (stopS + delayS) / durationS;

/** Each bulb phase starts a quarter cycle further in; glow group b starts half a pulse in. */
const bulbDelayS = (phase: number) => (phase * BULB_CYCLE_S) / 4;
const GLOW_B_DELAY_S = GLOW_PULSE_S / 2;

export const StyledHolidayLights = styled.svg`
  position: absolute;
  /* Both category bars have 6px of top padding above the tab; overlap the divider by 1px. */
  top: -7px;
  left: 50%;
  transform: translateX(-50%);
  overflow: visible;
  pointer-events: none;
  z-index: 1;

  /*
   * The mobile strip's tab sits space[stripPaddingYSpace] below the strip's top edge, plus its 1px
   * transparent border. Hanging from that edge keeps the wire inside the strip's scroll container,
   * whose overflow would otherwise clip it.
   */
  &.holiday-lights--strip {
    top: calc(-1 * ${({ theme }) => theme.space[MEGA_NAV_GEOMETRY.stripPaddingYSpace]} - 1px);
    /* Centre over the label and chevron together; the tab's side padding is uneven, so that isn't 50%. */
    left: calc(50% + ${pxToRem((MEGA_NAV_GEOMETRY.chipPaddingLeftPx - MEGA_NAV_GEOMETRY.chipPaddingRightPx) / 2)});
  }

  .holiday-lights__wire {
    fill: none;
    stroke: ${({ theme }) => theme.colors.mono7};
    stroke-width: 1;
    stroke-linecap: round;
  }

  .holiday-lights__socket {
    fill: ${({ theme }) => theme.colors.mono9};
  }

  .holiday-lights__glow {
    opacity: 0.45;
  }

  @media (prefers-reduced-motion: no-preference) {
    .holiday-lights__bulb {
      animation: ${bulbColorCycle} ${BULB_CYCLE_S}s linear forwards;
    }

    .holiday-lights__bulb--p0 {
      animation-delay: -${bulbDelayS(0)}s;
      animation-iteration-count: ${iterationsUntil(BULB_STOP_S, BULB_CYCLE_S, bulbDelayS(0))};
    }

    .holiday-lights__bulb--p1 {
      animation-delay: -${bulbDelayS(1)}s;
      animation-iteration-count: ${iterationsUntil(BULB_STOP_S, BULB_CYCLE_S, bulbDelayS(1))};
    }

    .holiday-lights__bulb--p2 {
      animation-delay: -${bulbDelayS(2)}s;
      animation-iteration-count: ${iterationsUntil(BULB_STOP_S, BULB_CYCLE_S, bulbDelayS(2))};
    }

    .holiday-lights__bulb--p3 {
      animation-delay: -${bulbDelayS(3)}s;
      animation-iteration-count: ${iterationsUntil(BULB_STOP_S, BULB_CYCLE_S, bulbDelayS(3))};
    }

    .holiday-lights__glow {
      animation: ${glowPulse} ${GLOW_PULSE_S}s ease-in-out forwards;
    }

    .holiday-lights__glow--a {
      animation-iteration-count: ${iterationsUntil(GLOW_STOP_S, GLOW_PULSE_S, 0)};
    }

    .holiday-lights__glow--b {
      animation-delay: -${GLOW_B_DELAY_S}s;
      animation-iteration-count: ${iterationsUntil(GLOW_STOP_S, GLOW_PULSE_S, GLOW_B_DELAY_S)};
    }
  }

  /*
   * Below md (1024px) the flat bar becomes a horizontally scrolling strip whose 6px top padding can't fit
   * the lights. \`down\` is exclusive of the *next* alias, so \`down('sm4')\` is < 1024; \`down('md')\` would be < 1152.
   */
  ${({ theme }) => theme.mediaQueries.down({ breakpointAlias: 'sm4' })} {
    &.holiday-lights--bar {
      display: none;
    }
  }
`;
